import styles from "./Scores.module.css";
import MathJax from "../../components/MathJax";
import { useDispatch, useSelector } from "react-redux";
import { TRootState } from "../../store/create-store";
import { generateScoresCacheKey } from "../../utils/utils";
import { TCorrelations } from "../../types";
import {
  ComponentProps,
  FC,
  FunctionComponent,
  useEffect,
  useState,
} from "react";
import classNames from "classnames";
import { selectLeftPanelState, selectRightPanelState } from "../../selectors";
import Icon from "../../components/Icon";
import {
  selectAreAllWorkersIdle,
  selectCorrelations,
  selectLinearRegression,
  selectLogisticRegression,
  selectSilhouetteScoreForCacheKey,
} from "../../selectors/worker.selector";
import {
  isColumnCategorical,
  selectConScores,
} from "../../selectors/root.selector";
import ScoreRow from "./ScoreRow";
import ReactTooltip from "react-tooltip";
import { workerActions } from "../../reducers/worker.slice";
import ColumnNameUtils from "../../utils/ColumnNameUtils";
import MathUtils from "../../utils/MathUtils";

const COLORS = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
].reverse();

type TShowMenu = {
  logitRegression: boolean;
  R: boolean;
  dCor: boolean;
  pCor: boolean;
  spearmansRho: boolean;
  kendallsTau: boolean;
};

type TCorrelationsProps = {
  showMenu: TShowMenu;
  setShowMenu: (showMenu: TShowMenu) => void;
  data: TCorrelations | null;
};

const renderItem = (item: ComponentProps<typeof ScoreRow>) => (
  <ScoreRow key={item.label} {...item} />
);

const Headers: Array<{
  label: string;
  key: keyof TShowMenu;
  tooltip: string;
}> = [
  {
    label: "dCor",
    key: "dCor",
    tooltip: "Distance Correlation",
  },
  {
    label: "pCor",
    key: "pCor",
    tooltip: "Pearson correlation coefficient",
  },
  {
    label: "ρ",
    key: "spearmansRho",
    tooltip: "Spearman's rank correlation coefficient",
  },
  {
    label: "τ_B",
    key: "kendallsTau",
    tooltip: "Kendall rank correlation coefficient",
  },
];

const Header: FunctionComponent<{
  disabled?: boolean;
  label: string;
  label2?: string;
  tooltip?: string;
  show: boolean;
  onClick?: () => void;
  className?: string;
  threeColumns?: boolean;
}> = (props) => (
  <tr>
    <td
      className={classNames(
        styles.sectionCell,
        {
          [styles.withInfo]: !!props.tooltip,
          [styles.disabled]: props.disabled,
        },
        props.className
      )}
      onClick={props.onClick}
    >
      <MathJax tex={props.label} />
      {props.label2 && <MathJax tex={props.label2} />}
      {props.tooltip && (
        <span data-tip={props.tooltip} className={styles.infoIcon}>
          <Icon name="info" outlined />
        </span>
      )}
    </td>

    <td
      className={classNames(
        styles.sectionCell,
        styles.caret,
        {
          [styles.disabled]: props.disabled,
        },
        props.className
      )}
      onClick={props.onClick}
    >
      {props.onClick && (
        <Icon name={props.show ? "expand_less" : "expand_more"} />
      )}
    </td>

    {props.threeColumns && <td className={styles.thirdHeaderCell}></td>}
  </tr>
);

const ScoreBar: FC<{
  width: number;
  background: string;
}> = ({ width, background }) => (
  <span
    className={styles.scoreBar}
    style={{
      background,
      width: `${width}px`,
    }}
  />
);

const ContinuousScores: FunctionComponent<TCorrelationsProps> = (props) => {
  const headerElements = Headers.map((h) => (
    <Header
      key={h.key}
      label={h.label}
      show={props.showMenu[h.key]}
      tooltip={h.tooltip}
      className={styles.subHeader}
      onClick={() =>
        props.setShowMenu({
          ...props.showMenu,
          [h.key]: !props.showMenu[h.key],
        })
      }
    />
  ));

  const distanceCor = [
    { label: "dCor_x{_,}{_v}", value: props.data?.dCor_x },
    { label: "dCor_y{_,}{_v}", value: props.data?.dCor_y },
    { label: "dCor_z{_,}{_v}", value: props.data?.dCor_z },
  ];

  const pearsonCor = [
    { label: "pCor_x{_,}{_v}^2", value: props.data?.r_sq_x },
    { label: "pCor_y{_,}{_v}^2", value: props.data?.r_sq_y },
    { label: "pCor_z{_,}{_v}^2", value: props.data?.r_sq_z },
  ];

  const spearmansRho = [
    { label: "ρ_x{_,}{_v}", value: props.data?.rho_x },
    { label: "ρ_y{_,}{_v}", value: props.data?.rho_y },
    { label: "ρ_z{_,}{_v}", value: props.data?.rho_z },
  ];

  const kendallsTau = [
    { label: "τ_x{_,}{_v}", value: props.data?.tau_xb },
    { label: "τ_y{_,}{_v}", value: props.data?.tau_yb },
    { label: "τ_z{_,}{_v}", value: props.data?.tau_zb },
  ];

  return (
    <>
      {headerElements[0]}
      {props.showMenu.dCor && distanceCor.map(renderItem)}
      {headerElements[1]}
      {props.showMenu.pCor && pearsonCor.map(renderItem)}
      {headerElements[2]}
      {props.showMenu.spearmansRho && spearmansRho.map(renderItem)}
      {headerElements[3]}
      {props.showMenu.kendallsTau && kendallsTau.map(renderItem)}
    </>
  );
};

const Scores = () => {
  const dispatch = useDispatch();
  const {
    showCorrelations,
    correlations,
    selectedColumn,
    logitRegression,
    linearRegression,
    isColumnCategorical,
    silhouetteScore,
    areAllWorkersIdle,
    selectedMethod,
    selectedDataset,
    conScores,
  } = useSelector(selectScores);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  const isAnyScoreMissing =
    !correlations || !logitRegression || !linearRegression || !silhouetteScore;

  useEffect(() => {
    if (areAllWorkersIdle && isAnyScoreMissing) {
      dispatch(workerActions.generateScores());
    }
  }, [
    areAllWorkersIdle,
    isAnyScoreMissing,
    selectedMethod,
    selectedDataset,
    selectedColumn,
  ]);

  const [showMenu, setShowMenu] = useState<TShowMenu>({
    logitRegression: true,
    R: false,
    dCor: false,
    pCor: false,
    spearmansRho: false,
    kendallsTau: false,
  });

  const [showSilhouette, setShowSilhouette] = useState(false);
  const [showContinuous, setShowContinuous] = useState(false);

  if (!showCorrelations) {
    return null;
  }

  const columnLabel = ColumnNameUtils.getLabelForMethodX(selectedMethod);
  const conScoresForMethod = Object.entries(conScores).filter(([key, value]) =>
    key.startsWith(columnLabel)
  );

  return (
    <div className={styles.wrapper}>
      <table>
        <tbody>
          <Header label="Con-score" show={true} threeColumns />

          {conScoresForMethod.map(([key, value], index) => {
            let percent = 0;
            let percentage = "-";
            // TODO column names case mismatch in csv
            const selectedColumnLower = selectedColumn.toLowerCase();
            const scoreForSelectedColumn = value[selectedColumnLower];
            if (Boolean(scoreForSelectedColumn)) {
              percent = MathUtils.percentage(
                Number(value[selectedColumnLower])
              );
              percentage = `${percent}\\%`;
            }

            return (
              <ScoreRow
                key={key}
                label={`${ColumnNameUtils.getConScoreLabel(key)}`}
                value={percentage}
                thirdCell={
                  <ScoreBar width={percent} background={COLORS[index]} />
                }
              />
            );
          })}
        </tbody>
      </table>
      <table>
        <tbody>
          {isColumnCategorical ? (
            <>
              {/*<ScoreRow*/}
              {/*  label="Logistic"*/}
              {/*  label2="Regression"*/}
              {/*  value={logitRegression?.balancedAccuracy}*/}
              {/*  decimals={2}*/}
              {/*  unit={`\\%`}*/}
              {/*/>*/}
              <Header
                label="Other\ metrics"
                show={showSilhouette}
                onClick={() => setShowSilhouette(!showSilhouette)}
                threeColumns
              />
              {showSilhouette && (
                <ScoreRow label="Silhouette Coeff" value={silhouetteScore} />
              )}
            </>
          ) : (
            <>
              {/*<ScoreRow*/}
              {/*  label="Linear"*/}
              {/*  label2="Regression (R^2)"*/}
              {/*  value={linearRegression?.coefOfDetermination}*/}
              {/*  decimals={2}*/}
              {/*  unit={`\\%`}*/}
              {/*/>*/}
              <Header
                label="Other\ metrics"
                show={showContinuous}
                onClick={() => setShowContinuous(!showContinuous)}
                threeColumns
              />
              {showContinuous && (
                <ContinuousScores
                  data={correlations}
                  showMenu={showMenu}
                  setShowMenu={setShowMenu}
                />
              )}
            </>
          )}
        </tbody>
      </table>

      {correlations &&
        (showMenu.R ||
          showMenu.pCor ||
          showMenu.dCor ||
          showMenu.spearmansRho ||
          showMenu.kendallsTau) && (
          <div className={styles.selectedColumnName}>
            <span>where&nbsp;</span>
            <MathJax tex={`v = `} />
            <span className={styles.columnName}>{selectedColumn}</span>
          </div>
        )}
    </div>
  );
};

const selectScores = (state: TRootState) => {
  const leftPanel = selectLeftPanelState(state);
  const rightPanel = selectRightPanelState(state);

  if (
    !leftPanel.selectedDataset ||
    !leftPanel.selectedMethod ||
    !leftPanel.selectedColumn
  ) {
    return {
      values: null,
      error: null,
    };
  }

  const cacheKey = generateScoresCacheKey(
    leftPanel.selectedDataset,
    leftPanel.selectedMethod,
    leftPanel.selectedColumn
  );

  return {
    cacheKey,
    showCorrelations: rightPanel.showCorrelations,
    selectedColumn: leftPanel.selectedColumn,
    correlations: selectCorrelations(state, cacheKey) ?? null,
    linearRegression: selectLinearRegression(state, cacheKey),
    logitRegression: selectLogisticRegression(state, cacheKey),
    silhouetteScore: selectSilhouetteScoreForCacheKey(state, cacheKey),
    isColumnCategorical: isColumnCategorical(state, leftPanel.selectedColumn),
    areAllWorkersIdle: selectAreAllWorkersIdle(state),
    conScores: selectConScores(state),
    selectedDataset: leftPanel.selectedDataset,
    selectedMethod: leftPanel.selectedMethod,
  };
};

export default Scores;
